<script>
  import moment from 'moment'
  import raf from 'raf'

  const _CLASS_DIGITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']

  const formatTimestamp = (ms) => {
    ms = parseFloat(ms)
    const mn = Math.floor(ms / 60000)
    const s = Math.round(ms / 1000 % 60)
    const h = Math.floor(ms / 60000 / 60)
    const m = h ? (mn % 60) : mn
    const hh = h === 0 ? '  ' : h < 10 ? ` ${h}` : `${h}`
    const mm = m === 0 ? ' 0' : m < 10 ? `${h ? 0 : ' '}${m}` : `${m}`
    const ss = s === 0 && !m ? '00' : s < 10 ? `0${s}` : `${s}`
    return `${hh}${mm}${ss}`
  }

  export default {
    name: 'DigitalClock',
    props: {
      value: {
        type: Boolean,
        default: false
      },
      auto: {
        type: Boolean,
        default: false
      },
      dark: {
        type: Boolean,
        default: false
      },
      timer: {
        type: Number,
        default: null
      },
      dense: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        time: '',
        dcTimer: 0,
        timerValue: 0,
        clockInstance: null,
        timerInstance: null
      }
    },

    computed: {
      h1() {
        return _CLASS_DIGITS[this.time[0]]
      },
      h2() {
        return _CLASS_DIGITS[this.time[1]]
      },
      m1() {
        return _CLASS_DIGITS[this.time[2]]
      },
      m2() {
        return _CLASS_DIGITS[this.time[3]]
      },
      s1() {
        return _CLASS_DIGITS[this.time[4]]
      },
      s2() {
        return _CLASS_DIGITS[this.time[5]]
      }
    },
    watch: {
      value() {
        this.value ? this.startTimer() : this.stopTimer()
      },
      timer() {
        this.setTimer(this.timer)
      }
    },

    mounted() {
      if (this.auto) this.clock()
      if (typeof this.timer === 'number') {
        this.timerValue = this.timer // store current timer prop
        this.setTimer(this.timer)
      }
    },
    beforeDestroy() {
      raf.cancel(this.clockInstance)
      raf.cancel(this.timerInstance)
    },

    methods: {
      clock() {
        this.time = moment().format('HHmmss')
        this.clockInstance = raf(this.clock.bind(this))
      },

      // calculate new digital clock timer timestamp
      setTimer(timer) {
        this.dcTimer = moment.now() - (timer * 1000) // timer is in seconds
        this.timerValue = timer
        this.refreshTimer()
      },
      // calculate new timer value (now - dc timer timestamp)
      updateTimer() {
        const ms = moment.now() - this.dcTimer
        const timerValue = Math.floor(ms / 1000) // use floor instead of round, 22977ms is not 23s
        if (this.timerValue !== timerValue) {
          this.timerValue = timerValue
          this.refreshTimer()
        }
      },
      // update display time
      // emit @change with new timer value
      refreshTimer() {
        this.time = formatTimestamp(this.timerValue * 1000)
        this.$emit('change', this.timerValue)
      },
      // start timer with request animation frame
      startTimer() {
        this.setTimer(this.timerValue) // set new dcTimer with current timer value
        this._running()
      },
      _running() {
        this.updateTimer()
        this.timerInstance = raf(this._running.bind(this))
      },
      // stop timer
      // cancel request animation frame
      stopTimer() {
        this.refreshTimer()
        if (this.timerInstance) {
          raf.cancel(this.timerInstance)
          this.timerInstance = null
        }
      },
      toggleTimer() {
        if (!this.disabled) this.$emit('toggle')
      }
    }
  }
</script>

<template>
  <div class="digits" :class="[dark ? 'dark' : 'light', dense ? 'dc--dense' : '', disabled ? 'dc--disabled' : '']" @click.left.stop="toggleTimer">
    <div :class="h1">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>
    <div :class="h2">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>

    <div class="dots" :class="{'d-invisible': !h2}"></div>

    <div :class="m1">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>
    <div :class="m2">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>

    <div class="dots" :class="{'d-invisible': !m2}"></div>

    <div :class="s1">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>
    <div :class="s2">
      <span v-for="digit in 7" :key="digit" :class="`digit${digit}`"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .digits {
    height: 48px;
    padding: 0 6px;
    &.dc--dense {
      padding: 0 2px;
    }
  }
  .digits div {
    position: relative;
    display: inline-block;
    width: 14px;
    height: 24px;
    margin: 12px 2px;
    text-align: left;
  }
  .digits.dc--dense div {
    width: 14px;
  }

  .digits div span {
    position: absolute;
    opacity: 0;
    -webkit-transition: 0.2s;
    -moz-transition: 0.2s;
    transition: 0.2s;
  }

  .digits div span::before,
  .digits div span::after {
    position: absolute;
    width: 0;
    height: 0;
    content: '';
    border: 5px solid transparent;
  }

  .digits .dots.d-invisible {
    visibility: hidden;
  }
  .digits .dots::before,
  .digits .dots::after {
    position: absolute;
    top: 8px;
    left: 6px;
    width: 3px;
    height: 3px;
    content: '';
  }
  .digits .dots::after {
    top: 20px;
  }

  /* Light color theme */
  .digits.light {
    color: rgba(0, 0, 0, 0.87);
    background-color: transparent;
  }
  .digits.light div span {
    background-color: rgba(0, 0, 0, 0.87);
    border-color: rgba(0, 0, 0, 0.87);
  }
  .digits.light .dots::before,
  .digits.light .dots::after {
    background-color: rgba(0, 0, 0, 0.87);
  }

  /* Dark color theme */
  .digits.dark {
    color: whitesmoke;
    background-color: transparent;
  }
  .digits.dark div span {
    background-color: whitesmoke;
    border-color: whitesmoke;
  }
  .digits.dark div.dots::before,
  .digits.dark div.dots::after {
    background-color: whitesmoke;
  }
  .digits.dark.dc--disabled {
    color: rgba(255, 255, 255, 0.7);
    background-color: transparent;
  }
  .digits.dark.dc--disabled div span {
    background-color: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.7);
  }
  .digits.dark.dc--disabled div.dots::before,
  .digits.dark.dc--disabled div.dots::after {
    background-color: rgba(255, 255, 255, 0.7);
  }

  .digits .digit1 {
    top: 0;
    left: 3px;
    width: 8px;
    height: 2px;
  }
  .digits .digit1::before {
    left: -2px;
    border-width: 0 2px 2px 0;
    border-right-color: inherit;
  }
  .digits .digit1::after {
    right: -2px;
    border-width: 0 0 2px 2px;
    border-left-color: inherit;
  }

  .digits .digit2 {
    top: 12px;
    left: 3px;
    width: 8px;
    height: 2px;
  }
  .digits .digit2::before {
    left: -4px;
    border-width: 1px 2px 1px;
    border-right-color: inherit;
  }
  .digits .digit2::after {
    right: -4px;
    border-width: 1px 2px 1px;
    border-left-color: inherit;
  }

  .digits .digit3 {
    top: 24px;
    left: 3px;
    width: 8px;
    height: 2px;
  }
  .digits .digit3::before {
    left: -2px;
    border-width: 2px 2px 0 0;
    border-right-color: inherit;
  }
  .digits .digit3::after {
    right: -2px;
    border-width: 2px 0 0 2px;
    border-left-color: inherit;
  }

  .digits .digit4 {
    top: 4px;
    left: 0;
    width: 2px;
    height: 7px;
  }
  .digits .digit4::before {
    top: -2px;
    border-width: 0 2px 2px 0;
    border-bottom-color: inherit;
  }
  .digits .digit4::after {
    bottom: -2px;
    border-width: 0 0 2px 2px;
    border-left-color: inherit;
  }

  .digits .digit5 {
    top: 4px;
    right: 0;
    width: 2px;
    height: 7px;
  }
  .digits .digit5::before {
    top: -2px;
    border-width: 0 0 2px 2px;
    border-bottom-color: inherit;
  }
  .digits .digit5::after {
    bottom: -2px;
    border-width: 2px 0 0 2px;
    border-top-color: inherit;
  }

  .digits .digit6 {
    top: 16px;
    left: 0;
    width: 2px;
    height: 7px;
  }
  .digits .digit6::before {
    top: -2px;
    border-width: 0 2px 2px 0;
    border-bottom-color: inherit;
  }
  .digits .digit6::after {
    bottom: -2px;
    border-width: 0 0 2px 2px;
    border-left-color: inherit;
  }

  .digits .digit7 {
    top: 16px;
    right: 0;
    width: 2px;
    height: 7px;
  }
  .digits .digit7::before {
    top: -2px;
    border-width: 0 0 2px 2px;
    border-bottom-color: inherit;
  }
  .digits .digit7::after {
    bottom: -2px;
    border-width: 2px 0 0 2px;
    border-top-color: inherit;
  }

  /* 1 */
  .digits div.one .digit5,
  .digits div.one .digit7 {
    opacity: 1;
  }

  /* 2 */
  .digits div.two .digit1,
  .digits div.two .digit5,
  .digits div.two .digit2,
  .digits div.two .digit6,
  .digits div.two .digit3 {
    opacity: 1;
  }

  /* 3 */
  .digits div.three .digit1,
  .digits div.three .digit5,
  .digits div.three .digit2,
  .digits div.three .digit7,
  .digits div.three .digit3 {
    opacity: 1;
  }

  /* 4 */
  .digits div.four .digit5,
  .digits div.four .digit2,
  .digits div.four .digit4,
  .digits div.four .digit7 {
    opacity: 1;
  }

  /* 5 */
  .digits div.five .digit1,
  .digits div.five .digit2,
  .digits div.five .digit4,
  .digits div.five .digit3,
  .digits div.five .digit7 {
    opacity: 1;
  }

  /* 6 */
  .digits div.six .digit1,
  .digits div.six .digit2,
  .digits div.six .digit4,
  .digits div.six .digit3,
  .digits div.six .digit6,
  .digits div.six .digit7 {
    opacity: 1;
  }

  /* 7 */
  .digits div.seven .digit1,
  .digits div.seven .digit5,
  .digits div.seven .digit7 {
    opacity: 1;
  }

  /* 8 */
  .digits div.eight .digit1,
  .digits div.eight .digit2,
  .digits div.eight .digit3,
  .digits div.eight .digit4,
  .digits div.eight .digit5,
  .digits div.eight .digit6,
  .digits div.eight .digit7 {
    opacity: 1;
  }

  /* 9 */
  .digits div.nine .digit1,
  .digits div.nine .digit2,
  .digits div.nine .digit3,
  .digits div.nine .digit4,
  .digits div.nine .digit5,
  .digits div.nine .digit7 {
    opacity: 1;
  }

  /* 0 */
  .digits div.zero .digit1,
  .digits div.zero .digit3,
  .digits div.zero .digit4,
  .digits div.zero .digit5,
  .digits div.zero .digit6,
  .digits div.zero .digit7 {
    opacity: 1;
  }
</style>
